import type { Cabal } from "../../types";

export const cabals: Cabal[] = [
  {
    id: "alchemy-guild",
    name: "The Alchemy Guild",
    description: [
      {
        type: "paragraph",
        text: "While Scholars of the Institute bother with stuffy academia and theoretical research, you're elbow-deep in science you can taste!",
      },
      {
        type: "paragraph",
        text: "It's not uncommon for Alchemists to be burnt, scarred, or slightly addled by self-testing their latest experimental cocktail, but there's no one more reliable for practical magic. After all, sometimes progress comes with a little risk!",
      },
      {
        type: "paragraph",
        text: "Luckily for you, there's also no one better than an Alchemist at figuring out ways around grievous injury, or even bypassing death itself...",
      },
    ],
    favouredPatronIds: ["tinker", "undying"],
    requirements:
      "Either the Gift of Alchemy or the Gift of Everlasting Life. At least one of these Gifts must be from a Favoured Patron.",
    costuming:
      "Overalls, pockets, gloves, goggles, vials, and trinkets. Even Alchemists that find themselves on the front lines testing out their latest inventions on their enemies will find a place in their armour for a notebook or specimen bag!",
  },
  {
    id: "arcane-institute",
    name: "The Arcane Institute",
    description: [
      {
        type: "paragraph",
        text: "Rigorous academics and researchers, Scholars of the Institute boast a plethora of certificates, authored papers, and titles, though they often vie with one another for research funding, which is tragically thin on the ground.",
      },
      {
        type: "paragraph",
        text: 'Referring to one another primarily as Scholars rather than the crude term "Warlock", they seek to understand and unravel the mysteries of the multiverse.',
      },
      {
        type: "paragraph",
        text: "Like magnets, gravity, and the power of love — how do they work!??",
      },
    ],
    favouredPatronIds: ["librarian", "magician"],
    requirements:
      "Either the Gift of Artifice or the Gift of Sorcery. At least one of these Gifts must be from a Favoured Patron.",
    costuming:
      "Scholars wear anything from comfy sweater vests and jackets with quills and notebooks stuffed into every pocket, to ostentatious graduation regalia. Warlocks still at the beginning of their studies may even show up to a Challnge of 13 in their school uniforms!",
  },
  {
    id: "bureau-of-eldritch-administration",
    name: "The Bureau of Eldritch Administration",
    description: [
      {
        type: "paragraph",
        text: "Accountants, bureaucrats, and statespeople — any venture involving Warlocks has someone from the Bureau behind the scenes making sure things happen.",
      },
      {
        type: "paragraph",
        text: "Warlocks are, by and large, a disorganised rabble of dysfunctional liabilities with more power than sense, and the Bureau is here to herd them in the right direction for the shared success of everyone.",
      },
    ],
    favouredPatronIds: ["sycophant", "trickster"],
    requirements:
      "Either Silver-Tongued Rascal, or a Wealth Level of 3+ (Wealthy, Noble, or Royalty).",
    costuming:
      "Practical, stylish, understated. Everyone knows you're probably filthy rich — no need to be overly garish.",
  },
  {
    id: "free-alliance",
    name: "The Free Alliance",
    description: [
      {
        type: "paragraph",
        text: "These Warlocks are all about independence.",
      },
      {
        type: "paragraph",
        text: "In fact, they feel so strongly about individuality, they have gathered a large following of like-minded individuals together who share the same philosophy — here in the Free Alliance, you're perfectly unique, just like everyone else!",
      },
      {
        type: "paragraph",
        text: "The Free Alliance ally themselves with other Cabals as they see fit, going wherever adventure takes them, and doing whatever jobs need doing.",
      },
      {
        type: "paragraph",
        text: "They are most certainly not pirates, just... freelancers.",
      },
    ],
    favouredPatronIds: ["fool", "gambler"],
    requirements: "None — the Free Alliance is open to all!",
    costuming:
      "Your individuality is very important, and Warlocks of the Free Alliance often go to great lengths to ensure they look like no one else.",
  },
  {
    id: "shadowblade-mercenary",
    name: "Shadowblade Mercenary Co. Ltd.",
    description: [
      {
        type: "paragraph",
        text: "If there's a profit in killing it, a Shadowblade is already there to do the job and collect the coin.",
      },
      {
        type: "paragraph",
        text: "The other Cabals may go on about how knowledge is power, or the pen is mightier than the sword, but they've probably never been stabbed in the head.",
      },
      {
        type: "paragraph",
        text: "Shrewd negotiators, expert swordmasters, and crack sharpshooters, the Shadowblades are employed across the multiverse as the premier bounty hunters, bodyguards, enforcers, and shock troops.",
      },
    ],
    favouredPatronIds: ["merchant", "warrior"],
    requirements:
      "Either Firearms Training or Weapon & Shield Training. At least one of your Otherworldly Gifts must be from a Favoured Patron.",
    costuming:
      'Shadowblades are often accused of being "edgy". What these softer Warlocks simply don\'t understand is that skulls and spikes are a vital part of any Shadowblade\'s wardrobe, and that black really does go with everything. And you can never have "too many" daggers, mum!',
  },
  {
    id: "white-mages",
    name: "The White Mages",
    description: [
      {
        type: "paragraph",
        text: "Not all Warlocks live up to the selfish, ambitious stereotype.",
      },
      {
        type: "paragraph",
        text: "The White Mages are a crucial support network, and are in the highest demand of any Cabal. They serve as royal advisors, healers, priests, and are a bedrock of any adventuring party worth its salt across the multiverse.",
      },
      {
        type: "paragraph",
        text: "White Mages work hard to maintain their reputation... because it's so much easier to get away with what you want when people immediately trust you.",
      },
    ],
    favouredPatronIds: ["benefactor", "chirurgeon"],
    requirements:
      "Either Practical Medicine, or the Gift of Protection. At least one of your Otherworldly Gifts must be from a Favoured Patron.",
    costuming:
      "White Mages in politically influential positions might don flowing white robes or ornate silver gowns. More practical Mages can be seen in splotched lab coats or blood-stained surgical overalls. White Mages otherwise have a wide variety of costumes, often in keeping with the cultural traditions of the society they come from, identifying themselves to others with a simple white sash or band.",
  },
  {
    id: "witches-coven",
    name: "The Witches' Coven",
    description: [
      {
        type: "paragraph",
        text: "Anyone regardless of gender can be a Witch: all you need is an aura of mystery, a connection to the natural world, and a nice pointy hat.",
      },
      {
        type: "paragraph",
        text: "Witches of the Coven are experts at manipulating events from the shadows, and rarely reveal their true powers. If you anger a Witch, you might not even realise it for days or weeks later, once your crops mysteriously fail and your personal life falls apart, and you're left wondering how it came to this?",
      },
      {
        type: "paragraph",
        text: "Witches have an especially powerful connection to Fey and Undead creatures. These beings are more likely to trust and confide in a Witch as opposed to other Warlocks, and in turn, Witches take on a responsibility to ensure that wandering souls are laid to rest, and the Fey spirits of the natural world are appeased.",
      },
    ],
    favouredPatronIds: ["prophets"],
    requirements:
      "Either the Gift of the Familiar or the Gift of Sorcery. At least one of these Gifts must be from the Prophets.",
    costuming:
      "While no two Witches are truly alike, the pointy hat is a simply indispensable symbol of Witchdom. Plus, it keeps the rain and sun out of your eyes wonderfully, and makes it easy to find fellow Witches in a crowd! The size of the pointy hat, of course, doesn't matter.",
  },
];
